import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core'
import { TableSortEvent } from '@vg-constellation/patterns-angular/data-table'
import { debounceTime, distinctUntilChanged, timer } from 'rxjs'
import { FundLiteratureContentService } from 'src/app/services/fund-literature/fund-literature.service'
import { adobeLaunch } from 'vg-iig-adobe-launch'

import {
  DOWNLOAD_ACTIVATED,
  DOWNLOAD_DEACTIVATED,
  FUND_LITERATURE,
  FUND_NAME,
  NOT_AVAILABLE,
  PERCENT_SIGN,
  SESSION_STORAGE_CONSTANTS_CAAS_CONTENT,
} from '../../../constants/constant.content'
import {
  NUMBER_EIGHT,
  NUMBER_HUNDRED,
  NUMBER_TWO_HUNDRED,
} from '../../../constants/constant.numbers'
import { PRODUCT_DETAILS_URL } from '../../../constants/constant.urls'
import { IDisplayTableModel } from '../../models/display-table-model'
import { LiteratureModel } from '../../models/literature-model'
import { AbstractFilterProcess } from '../../services/data-filters/abstract-filter-process'
import { IFilterState } from '../../services/data-filters/models/ifilter-state'
import { ObservableService } from '../../services/observable-service/observable.service'
import { PolarisDataService } from '../../services/polaris-service/polaris-data-service'
import { SharedClassService } from '../../services/shared-class/shared-class.service'
import { UtilityService } from '../../services/utility-service/utility-service.service'
import { FilterModel } from '../../shared/filter-model'

@Component({
  selector: 'fund-literature-table',
  templateUrl: './fund-literature-table.component.html',
  styleUrls: ['./fund-literature-table.component.scss'],
})
export class FundLiteratureTableComponent implements OnInit {
  @Input() literatureTableLabelCaas: any
  isShowTable = false
  isShowLoader = true
  aemCassDataFromSession: any
  productList: any
  isSharedClass = false
  fundLiteratureData: IDisplayTableModel[] = []
  updatedFundLiteratureData: IDisplayTableModel[] = []
  isServiceAvailable = true
  retirementSavingsTrustShareClassesList = []
  fundLitFinalAry: LiteratureModel[] = []
  sortBy = FUND_NAME
  percentSign = PERCENT_SIGN
  productDetails = PRODUCT_DETAILS_URL
  selectedPdf: Map<string, any> = new Map<string, any>()
  literatureObjListForAdobe = []
  downloadStatus: any = {}
  @ViewChild('pdfContainer', { static: false }) pdfContainer: ElementRef
  announcement: string
  updateMessage: string
  NOT_AVAILABLE = NOT_AVAILABLE

  utilityService: UtilityService
  sharedClass: SharedClassService
  filterModel: FilterModel
  constructor(
    private readonly fd3FundLiteratureContentService: FundLiteratureContentService,
    private readonly polarisDataService: PolarisDataService,
    private readonly observableService: ObservableService,
    private readonly filterProcess: AbstractFilterProcess,
  ) {
    this.sharedClass = inject(SharedClassService)
    this.utilityService = inject(UtilityService)
    this.filterModel = inject(FilterModel)
  }

  ngOnInit() {
    this.retrieveSessionStorageData()
    this.setupSearchEventSubscription()
    this.setupFilterStateSubscription()
  }

  private retrieveSessionStorageData() {
    const aemCassDataFromSession = sessionStorage.getItem(
      SESSION_STORAGE_CONSTANTS_CAAS_CONTENT,
    )
    this.literatureTableLabelCaas = JSON.parse(
      aemCassDataFromSession,
    ).data.fd3tablecontentList.items[0]
  }

  private setupSearchEventSubscription() {
    this.observableService.searchSelectedValue
      .pipe(debounceTime(NUMBER_HUNDRED), distinctUntilChanged())
      .subscribe((searchText: string) => {
        this.filterProcess.setSearchText(searchText)
        this.applyFilter()
        this.updateAnnouncement()
      })
  }

  private setupFilterStateSubscription() {
    this.productList = this.polarisDataService.getProductList()
    this.observableService.consolidateFiltersObservable.subscribe(
      (filterState: IFilterState) => {
        this.isSharedClass = filterState.isShareClassSelected()
        if (this.isSharedClass === null) {
          this.isSharedClass = false
        }
        this.literatureSubscriberCallback(
          filterState.getDisplaySelection() || '',
        )
      },
    )
  }

  literatureSubscriberCallback(viewType: string) {
    if (viewType === FUND_LITERATURE) {
      this.retrieveLiteratureDataList()
    } else {
      this.setTableAndLoaderVisibility(false, false)
    }
  }

  private retrieveLiteratureDataList() {
    const priceData = this.productList
    const literatureData =
      this.fd3FundLiteratureContentService.getFundLiteratureData()

    if (!priceData) {
      this.setTableAndLoaderVisibility(false, false)
      return
    }

    if (this.isValidLiteratureData(literatureData)) {
      this.getLiteratureData(priceData, literatureData)
    } else {
      this.resetToDefault()
    }
  }

  private isValidLiteratureData(literatureData: any): boolean {
    return (
      literatureData &&
      literatureData.STRATEGY_DOCUMENT &&
      Array.isArray(literatureData.STRATEGY_DOCUMENT) &&
      literatureData.STRATEGY_DOCUMENT.length > 0
    )
  }

  private setTableAndLoaderVisibility(showTable: boolean, showLoader: boolean) {
    this.isShowTable = showTable
    this.isShowLoader = showLoader
  }

  private resetToDefault() {
    this.fundLiteratureData = []
    this.setTableAndLoaderVisibility(true, false)
    this.isServiceAvailable = false
  }

  getLiteratureData(priceData: any, litData: any) {
    this.retirementSavingsTrustShareClassesList =
      this.getRetirementSavingsTrustShareClassesList(priceData)
    this.updatedFundLiteratureData = this.addPriceDataToLiteratureObject(
      this.getFundLitMap(litData),
      this.getPriceDataMap(priceData),
    )
    this.applyFilter()
    this.updateAnnouncement()
  }

  private getRetirementSavingsTrustShareClassesList(priceData: any): any[] {
    return priceData.reduce((acc: any[], priceObject: any) => {
      const result =
        this.sharedClass.retirementSavingsTrustShareClasses(priceObject)
      return [...acc, ...result]
    }, [])
  }

  applyFilter() {
    const filteredIDisplayModelArray = this.getSharedClassData(
      this.updatedFundLiteratureData,
    )
    const view = this.filterProcess.applyFilter(filteredIDisplayModelArray)
    this.fundLiteratureData = view.getDispalyTableModelAry()
    this.setTableAndLoaderVisibility(true, false)
    this.observableService.changeRowRecordValue(this.fundLiteratureData.length)
    this.onFilterChangeSelectPdf()
    this.updateAnnouncement()
  }

  private addPriceDataToLiteratureObject(
    literatureMap: Map<string, LiteratureModel>,
    priceDataMap: Map<string, any>,
  ): LiteratureModel[] {
    const fundLiteratureData: LiteratureModel[] = []

    priceDataMap.forEach((priceObjectValue: any, portIdAsKey: string) => {
      if (
        !this.isSharedClass &&
        !this.sharedClass.checkForShareClasses(priceObjectValue)
      ) {
        return
      }

      if (priceObjectValue) {
        this.isServiceAvailable = true
        let literatureModel =
          literatureMap.get(portIdAsKey) ||
          this.createNewLiteratureModel(portIdAsKey)

        literatureModel = this.updateLiteratureModel(
          literatureModel,
          priceObjectValue,
        )
        fundLiteratureData.push(literatureModel)
      }
    })

    this.fundLitFinalAry = fundLiteratureData
    this.isShowLoader = false
    return fundLiteratureData
  }

  private createNewLiteratureModel(portId: string): LiteratureModel {
    const literatureModel = new LiteratureModel()
    literatureModel.portId = portId
    literatureModel.fundID = portId
    return literatureModel
  }

  private updateLiteratureModel(
    literatureModel: LiteratureModel,
    priceObjectValue: any,
  ): LiteratureModel {
    literatureModel.fundID = literatureModel.portId
    literatureModel.ticker = priceObjectValue.ticker
    literatureModel.symbol = priceObjectValue.ticker
    literatureModel.longNm = priceObjectValue.fundFullName
    literatureModel.fundName = priceObjectValue.fundFullName
    literatureModel.reedemptionFeeDisplay = priceObjectValue.redemptionFee
    literatureModel.purchaseFeeDisplay = priceObjectValue.purchaseFee
    literatureModel.isClosedFund = priceObjectValue.isClosedFund
    literatureModel.rowId = priceObjectValue.ticker
    return literatureModel
  }

  // eslint-disable-next-line max-statements
  private getFundLitMap(literatureList: {
    STRATEGY_DOCUMENT: string | any[]
  }): Map<string, LiteratureModel> {
    const fundLitMap: Map<string, LiteratureModel> = new Map<
      string,
      LiteratureModel
    >()
    const literatureListLen: number = literatureList.STRATEGY_DOCUMENT.length
    let pdfListArray = []

    for (let index = 0; index < literatureListLen; index++) {
      const litStrategy = literatureList.STRATEGY_DOCUMENT[index]
      const fundNumbers = this.utilityService.convertLitDataStrToArray(
        litStrategy.FUND_NUMBERS,
      )
      const investmentCommentary = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_URLS_IC,
      )
      const investmentCommentaryDate =
        this.utilityService.convertLitDataStrToArray(litStrategy.LIT_DATES_IC)
      const investmentProfile = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_URLS_IN,
      )
      const invProfDate = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_DATES_IN,
      )
      const factSheet = this.utilityService.convertLitDataStrToArray(
        litStrategy.FACT_SHEET_URLS,
      )
      const factSheetDate = this.utilityService.convertLitDataStrToArray(
        litStrategy.FACT_SHEET_DATES,
      )
      const summary = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_URLS_SP,
      )
      const prospectus = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_URLS_PR,
      )
      const sai = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_URLS_PB,
      )
      const planParticipant = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_URLS_PP,
      )
      const semiAnnualReport = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_URLS_SA,
      )
      const semiAnnualReportDate = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_DATES_SA,
      )
      const annualReport = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_URLS_AR,
      )
      const annualReportDate = this.utilityService.convertLitDataStrToArray(
        litStrategy.LIT_DATES_AR,
      )
      const fundCount = fundNumbers.length

      for (let fundIndex = 0; fundIndex < fundCount; fundIndex++) {
        const literatureObject = this.createLiteratureModel(
          fundNumbers[fundIndex],
          investmentCommentary[fundIndex],
          investmentCommentaryDate[fundIndex],
          investmentProfile[fundIndex],
          invProfDate[fundIndex],
          factSheet[fundIndex],
          factSheetDate[fundIndex],
          summary[fundIndex],
          prospectus[fundIndex],
          sai[fundIndex],
          planParticipant[fundIndex],
          semiAnnualReport[fundIndex],
          semiAnnualReportDate[fundIndex],
          annualReport[fundIndex],
          annualReportDate[fundIndex],
        )

        fundLitMap.set(literatureObject.portId, literatureObject)
        pdfListArray = this.addPdf(pdfListArray, literatureObject)
      }
    }
    this.fd3FundLiteratureContentService.finalPdfList = pdfListArray
    return fundLitMap
  }

  addPdf(pdfListArray: any[], literatureObject: LiteratureModel): any[] {
    pdfListArray = this.getAddedPdfList(
      pdfListArray,
      literatureObject.investmentCommentary,
    )
    pdfListArray = this.getAddedPdfList(
      pdfListArray,
      literatureObject.investmentProfile,
    )
    pdfListArray = this.getAddedPdfList(
      pdfListArray,
      literatureObject.factSheet,
    )
    pdfListArray = this.getAddedPdfList(
      pdfListArray,
      literatureObject.prospectus,
    )
    pdfListArray = this.getAddedPdfList(pdfListArray, literatureObject.sai)
    pdfListArray = this.getAddedPdfList(
      pdfListArray,
      literatureObject.summaryVal,
    )
    pdfListArray = this.getAddedPdfList(
      pdfListArray,
      literatureObject.semiAnnualReport,
    )
    pdfListArray = this.getAddedPdfList(
      pdfListArray,
      literatureObject.annualReport,
    )
    return pdfListArray
  }

  // eslint-disable-next-line max-statements,max-params
  private createLiteratureModel(
    portId: string,
    investmentCommentary: string,
    investmentCommentaryDate: string,
    investmentProfile: string,
    invProfDate: string,
    factSheet: string,
    factSheetDate: string,
    summary: string,
    prospectus: string,
    sai: string,
    planParticipant: string,
    semiAnnualReport: string,
    semiAnnualReportDate: string,
    annualReport: string,
    annualReportDate: string,
  ): LiteratureModel {
    const literatureObject = new LiteratureModel()
    literatureObject.portId = portId
    literatureObject.investmentCommentary = this.handleLiteratureContentVal(
      investmentCommentary.substring(NUMBER_EIGHT),
    )
    literatureObject.invComDate = this.handleLiteratureContentVal(
      investmentCommentaryDate,
    )
    literatureObject.investmentProfile = this.handleLiteratureContentVal(
      investmentProfile.substring(NUMBER_EIGHT),
    )
    literatureObject.invProfDate = this.handleLiteratureContentVal(invProfDate)
    literatureObject.factSheet = factSheet
    literatureObject.factSheetDate =
      this.handleLiteratureContentVal(factSheetDate)
    literatureObject.summaryVal = this.handleLiteratureContentVal(summary)
    literatureObject.prospectus = this.handleLiteratureContentVal(prospectus)
    literatureObject.sai = this.handleLiteratureContentVal(sai)
    literatureObject.planParticipant =
      this.handleLiteratureContentVal(planParticipant)
    literatureObject.comparison = null
    literatureObject.comparisonDate = null
    literatureObject.semiAnnualReport =
      this.handleLiteratureContentVal(semiAnnualReport)
    literatureObject.semiAnnualReportDate = semiAnnualReportDate
    literatureObject.annualReport =
      this.handleLiteratureContentVal(annualReport)
    literatureObject.annualReportDate = annualReportDate
    return literatureObject
  }

  private handleLiteratureContentVal(contentValue: string): string {
    return contentValue === PERCENT_SIGN ? undefined : contentValue
  }

  getAddedPdfList(pdfListArray: any[], url: string) {
    if (url !== null && url !== '') {
      if (!pdfListArray.includes(url)) {
        pdfListArray.push(url)
      }
    }
    return pdfListArray
  }

  private getPriceDataMap(priceDataList: any): Map<string, any> {
    return priceDataList.reduce(
      (priceDataMap: Map<string, any>, priceDataObj: any) => {
        const trimFund = this.utilityService.trimPolarisFundName(priceDataObj)
        priceDataObj.fundFullName = trimFund.fundName
        priceDataObj.ticker = trimFund.fundSymbol
        priceDataObj.isClosedFund =
          this.utilityService.getClosedIndicatorForFund(priceDataObj)
        priceDataObj = this.utilityService.setPolarisFees(
          priceDataObj,
          priceDataObj,
        )
        priceDataMap.set(priceDataObj.portId, priceDataObj)
        return priceDataMap
      },
      new Map<string, any>(),
    )
  }

  getSharedClassData(fundLiteratureData: any[]): LiteratureModel[] {
    if (this.isSharedClass) {
      return fundLiteratureData
    }

    this.setTableAndLoaderVisibility(false, true)
    this.sharedClass.retirementSavingsTrustShareClassesList =
      this.retirementSavingsTrustShareClassesList

    return fundLiteratureData.filter((data) =>
      this.sharedClass.checkForShareClasses(data),
    ) as LiteratureModel[]
  }

  handleSortEvent({ columnName, isAscending }: TableSortEvent): void {
    // Get the filtered column value
    columnName = this.filterModel.getSortColumnValue(columnName)
    // If the sorting is not ascending, prepend a minus sign to the column name
    if (!isAscending) {
      columnName = `-${columnName}`
    }
    // Set the sortBy property to the column name
    this.sortBy = columnName
  }

  /**Select toggle the checkbox and adding into selectedPdf set delete for download*/
  // eslint-disable-next-line max-params
  selectPdf(url: any, id: string, key: any, longNm: any, event: any) {
    if (event.target.checked) {
      this.selectedPdf.set(id, { url: url, key: key, fundName: longNm })
      this.announcement = DOWNLOAD_ACTIVATED
    } else {
      this.selectedPdf.delete(id)
      if (this.selectedPdf.size === 0) {
        this.announcement = DOWNLOAD_DEACTIVATED
      }
    }
  }

  /** Calling on filter state change
   * If individual is selected before filter state change then set value to check box.*/
  onFilterChangeSelectPdf() {
    /*waiting to DOM get ready*/
    timer(NUMBER_TWO_HUNDRED).subscribe(() => {
      for (const [id] of this.selectedPdf) {
        const element = this.pdfContainer.nativeElement.querySelector(`#${id}`)
        if (element) {
          element.checked = true
        } else {
          this.selectedPdf.delete(id)
        }
      }
    })
  }

  downloadPDF() {
    if (this.selectedPdf.size === 0) {
      return false
    }
    const downloadList = []
    this.observableService.coverSpinnerTrigger(true)
    this.selectedPdf.forEach((value) => {
      if (value.url && typeof value.url === 'string') {
        downloadList.push({
          fileName: value.fundName,
          url: value.url,
          key: value.key,
        })

        const fileName = value.url
          .split('/')
          .pop()
          .split('.')[0]
          .replace(/\+/g, ' ')
        const pdfName = `PDF:${fileName}`
        this.literatureObjListForAdobe.push({
          file: {
            fileLocation: window.location.href,
            fileName: pdfName,
          },
        })
      } else {
        console.error('Invalid URL:', value.url)
      }
    })
    this.fd3FundLiteratureContentService
      .savePdf(downloadList)
      .subscribe((status) => {
        this.downloadStatus = status
        this.observableService.coverSpinnerTrigger(false)
        this.uncheckAllPDF()
      })
    adobeLaunch.trackMultipleContentDownloaded(this.literatureObjListForAdobe)
  }

  uncheckAllPDF() {
    for (const [id] of this.selectedPdf) {
      const element = this.pdfContainer.nativeElement.querySelector(`#${id}`)
      if (element) {
        element.checked = false
      }
    }
    this.selectedPdf = new Map<string, any>()
  }

  updateAnnouncement() {
    this.updateMessage = `Table updated. ${this.fundLiteratureData.length} rows now displayed.`
  }
}
